/**
 * The 7d pace basis from server-side history (US 34691). Bug 34688 made the 7d
 * projection a slope over at least the last 12h, but the samples lived only in
 * the browser session: a fresh wallboard read the since-open average for 12h,
 * and the Roost Seats and Accounts pages never had a 12h basis at all. The
 * bridge now keeps weekly readings (GET /ryn-db/quota/samples); every surface
 * builds its samples here, so they share one basis.
 */
import type {
  QuotaRow,
  QuotaSampleRow,
} from '../models/flock-telemetry.models';
import {
  aggregateQuotaRollups,
  parseUtcMs,
} from '../models/flock-telemetry.utils';
import {
  PaceSample,
  PaceSampleMap,
  WindowKey,
  paceKey,
  retainPaceSamples,
} from './pace.logic';
import { type CcAttention, resetToMs } from './attention-inputs.logic';

/** History requested on load: more than the 16h the 7d sample list keeps. */
export const PACE_HISTORY_HOURS = 24;
/** Re-read the history this often; the server thins to one per 15 min. */
export const PACE_HISTORY_REFRESH_MS = 15 * 60_000;

/** One reading into a window's list: a repeat of the same instant is replaced,
 * then the list is bounded to the window's lookback (Bug 34814). */
function addPaceSample(
  list: readonly PaceSample[],
  sample: PaceSample,
  window: WindowKey,
  nowMs: number
): PaceSample[] {
  return retainPaceSamples(
    [...list.filter((s) => s.atMs !== sample.atMs), sample],
    window,
    nowMs
  );
}

/** Stored Claude weekly readings into the 7d sample lists. */
export function seedPaceHistory(
  samples: PaceSampleMap,
  rows: readonly QuotaSampleRow[],
  nowMs: number
): PaceSampleMap {
  const next: PaceSampleMap = new Map(samples);
  for (const r of rows) {
    if (r.windowType !== 'Weekly' || (r.provider ?? 'claude') !== 'claude')
      continue;
    const atMs = parseUtcMs(r.sampledAt);
    if (atMs === null) continue;
    const k = paceKey(r.accountUpn, '7d');
    next.set(
      k,
      addPaceSample(
        next.get(k) ?? [],
        { atMs, pct: r.percentUsed },
        '7d',
        nowMs
      )
    );
  }
  return next;
}

/** Each account's current 5h and 7d readings, as the rollup reports them. */
export function addRollupPaceSamples(
  samples: PaceSampleMap,
  rows: QuotaRow[],
  nowMs: number
): PaceSampleMap {
  const next: PaceSampleMap = new Map(samples);
  const windows: [WindowKey, 'fiveHour' | 'weekly'][] = [
    ['5h', 'fiveHour'],
    ['7d', 'weekly'],
  ];
  for (const r of aggregateQuotaRollups(rows, nowMs)) {
    for (const [key, field] of windows) {
      const w = r[field];
      const reset = parseUtcMs(w.effectiveResetAt);
      if (reset === null || reset <= nowMs) continue;
      const atMs = parseUtcMs(w.capturedAt ?? null) ?? nowMs;
      const k = paceKey(r.accountUpn, key);
      next.set(
        k,
        addPaceSample(
          next.get(k) ?? [],
          { atMs, pct: w.percentUsed },
          key,
          nowMs
        )
      );
    }
  }
  return next;
}

/** A page with no session history (Roost Seats, Accounts): the stored history
 * plus the current readings, the same basis the wallboard builds. */
export function paceSamplesFor(
  history: readonly QuotaSampleRow[],
  quotaRows: QuotaRow[],
  nowMs: number
): PaceSampleMap {
  return addRollupPaceSamples(
    seedPaceHistory(new Map(), history, nowMs),
    quotaRows,
    nowMs
  );
}

/** True when the history was never read or is older than the refresh time. */
export function paceHistoryDue(
  lastReadAtMs: number | null,
  nowMs: number
): boolean {
  return (
    lastReadAtMs === null || nowMs - lastReadAtMs >= PACE_HISTORY_REFRESH_MS
  );
}

/**
 * The CC's live statusline readings (one per seat with an account) as pace
 * samples, so they feed the recent-burn slope too. Moved out of
 * WallboardStore (WI 34807) unchanged.
 */
export function addCcPaceSamples(
  samples: PaceSampleMap,
  a: CcAttention,
  nowMs: number
): PaceSampleMap {
  const atMs = parseUtcMs(a.at) ?? nowMs;
  const next: PaceSampleMap = new Map(samples);
  for (const s of a.seats) {
    if (!s.account) continue;
    const readings: [WindowKey, number | null, number | string | null][] = [
      ['5h', s.rate5h, s.resets5h],
      ['7d', s.rate7d, s.resets7d],
    ];
    for (const [key, pct, reset] of readings) {
      const resetMs = resetToMs(reset);
      if (pct === null || resetMs === null || resetMs <= atMs) continue;
      const k = paceKey(s.account, key);
      const list = (next.get(k) ?? []).filter((x) => x.atMs !== atMs);
      list.push({ atMs, pct });
      next.set(k, retainPaceSamples(list, key, nowMs));
    }
  }
  return next;
}
